import React, { useState, useEffect } from 'react'
import { 
  ArrowLeft,
  Save,
  RotateCcw
} from 'lucide-react'
import { Breadcrumb } from '../../components/Common'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import employeeMasterService from '../../services/employeeMasterService'

const EmployeeMasterForm = () => {
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    employeeCode: '',
    employeeName: '',
    email: '',
    phone: '',
    department: '',
    designation: '',
    expenseLimits: {
      dailyLimit: 0,
      monthlyLimit: 0,
      perTransactionLimit: 0,
      currency: 'USD'
    },
    approver: '',
    approverName: '',
    status: 'Active',
    employeeType: 'Full-time',
    joiningDate: '',
    address: {
      street: '',
      city: '',
      state: '',
      country: '',
      zipCode: ''
    },
    notes: ''
  })
  const [isLoadingEmployeeCode, setIsLoadingEmployeeCode] = useState(true)

  // Fetch next employee code
  useEffect(() => {
    const fetchEmployeeCode = async () => {
      try {
        setIsLoadingEmployeeCode(true)
        const response = await employeeMasterService.getNextEmployeeCode()
        if (response.success && response.data) {
          setFormData(prev => ({
            ...prev,
            employeeCode: response.data.employeeCode || 'EMP0001'
          }))
        }
      } catch (error) {
        console.error('Error fetching employee code:', error)
        setFormData(prev => ({ ...prev, employeeCode: 'EMP0001' }))
      } finally {
        setIsLoadingEmployeeCode(false)
      }
    }
    fetchEmployeeCode()
  }, [])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    if (name.startsWith('expenseLimits.')) {
      const limitField = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        expenseLimits: {
          ...prev.expenseLimits,
          [limitField]: limitField === 'currency' ? value : parseFloat(value) || 0
        }
      }))
    } else if (name.startsWith('address.')) {
      const addressField = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value
        }
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }))
    }
  }

  const buildPayload = () => {
    const payload = { ...formData }
    if (!payload.approver) delete payload.approver
    if (!payload.joiningDate) delete payload.joiningDate
    if (!payload.approverName) delete payload.approverName
    return payload
  }

  const handleSave = async () => {
    try {
      // Validation
      if (!formData.employeeName || !formData.email) {
        toast.error('Please fill in all required fields')
        return
      }

      const response = await employeeMasterService.createEmployee(buildPayload())
      if (response.success) {
        toast.success('Employee created successfully!')
        navigate('/expenses/employee-master')
      } else {
        toast.error(response.message || 'Failed to create employee')
      }
    } catch (error) {
      console.error('Error saving employee:', error)
      if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Invalid data provided')
      } else {
        toast.error('Failed to create employee')
      }
    }
  }

  const handleReset = () => {
    setFormData({
      employeeCode: formData.employeeCode, // Keep the code
      employeeName: '',
      email: '',
      phone: '',
      department: '',
      designation: '',
      expenseLimits: {
        dailyLimit: 0,
        monthlyLimit: 0,
        perTransactionLimit: 0,
        currency: 'USD'
      },
      approver: '',
      approverName: '',
      status: 'Active',
      employeeType: 'Full-time',
      joiningDate: '',
      address: {
        street: '',
        city: '',
        state: '',
        country: '',
        zipCode: ''
      },
      notes: ''
    })
  }

  const breadcrumbItems = [
    { label: 'Expenses', path: '/expenses', onClick: () => navigate('/expenses') },
    { label: 'Employee Master', path: '/expenses/employee-master', onClick: () => navigate('/expenses/employee-master') },
    { label: 'New Employee', path: '/expenses/employee-master/form', isCurrent: true }
  ]

  return (
    <div className="sales-dashboard">
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="page-header">
        <div className="page-title">
          <h1>Employee Master</h1>
          <p>Create new employee profile</p>
        </div>
        <div className="page-actions">
          <button className="btn btn-secondary" onClick={() => navigate('/expenses/employee-master')}>
            <ArrowLeft size={16} />
            Back
          </button>
        </div>
      </div>

      <div className="form-container">
        <div className="sac-form-header">
          <h2>Employee Information</h2>
        </div>
          
        <form className="form-content">
          <div className="section-header">
            <h3>Basic Information</h3>
          </div>

          {/* Row 1: Employee Code, Employee Name, Email, Phone */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="employeeCode" className="form-label required">
                Employee Code
              </label>
              <input
                type="text"
                id="employeeCode"
                name="employeeCode"
                value={isLoadingEmployeeCode ? 'Generating...' : formData.employeeCode}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="employeeName" className="form-label required">
                Employee Name
              </label>
              <input
                type="text"
                id="employeeName"
                name="employeeName"
                value={formData.employeeName}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter employee name"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="email" className="form-label required">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter email address"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone" className="form-label">
                Phone
              </label>
              <input
                type="text"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter phone number"
              />
            </div>
          </div>

          {/* Row 2: Department, Designation, Status, Employee Type */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="department" className="form-label">
                Department
              </label>
              <input
                type="text"
                id="department"
                name="department"
                value={formData.department}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter department"
              />
            </div>

            <div className="form-group">
              <label htmlFor="designation" className="form-label">
                Designation
              </label>
              <input
                type="text"
                id="designation"
                name="designation"
                value={formData.designation}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter designation"
              />
            </div>

            <div className="form-group">
              <label htmlFor="status" className="form-label required">
                Status
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="form-input"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="employeeType" className="form-label">
                Employee Type
              </label>
              <select
                id="employeeType"
                name="employeeType"
                value={formData.employeeType}
                onChange={handleInputChange}
                className="form-input"
              >
                <option value="Full-time">Full-time</option>
                <option value="Part-time">Part-time</option>
                <option value="Contract">Contract</option>
                <option value="Intern">Intern</option>
              </select>
            </div>
          </div>

          <div className="form-section-divider">
            <div className="section-header">
              <h3>Expense Limits</h3>
            </div>
          </div>

          {/* Row 3: Expense Limits */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="expenseLimits.dailyLimit" className="form-label">
                Daily Limit
              </label>
              <input
                type="number"
                id="expenseLimits.dailyLimit"
                name="expenseLimits.dailyLimit"
                value={formData.expenseLimits.dailyLimit}
                onChange={handleInputChange}
                className="form-input"
                min="0"
                step="0.01"
                placeholder="0.00"
              />
            </div>

            <div className="form-group">
              <label htmlFor="expenseLimits.monthlyLimit" className="form-label">
                Monthly Limit
              </label>
              <input
                type="number"
                id="expenseLimits.monthlyLimit"
                name="expenseLimits.monthlyLimit"
                value={formData.expenseLimits.monthlyLimit}
                onChange={handleInputChange}
                className="form-input"
                min="0"
                step="0.01"
                placeholder="0.00"
              />
            </div>

            <div className="form-group">
              <label htmlFor="expenseLimits.perTransactionLimit" className="form-label">
                Per Transaction Limit
              </label>
              <input
                type="number"
                id="expenseLimits.perTransactionLimit"
                name="expenseLimits.perTransactionLimit"
                value={formData.expenseLimits.perTransactionLimit}
                onChange={handleInputChange}
                className="form-input"
                min="0"
                step="0.01"
                placeholder="0.00"
              />
            </div>

            <div className="form-group">
              <label htmlFor="expenseLimits.currency" className="form-label">
                Currency
              </label>
              <select
                id="expenseLimits.currency"
                name="expenseLimits.currency"
                value={formData.expenseLimits.currency}
                onChange={handleInputChange}
                className="form-input"
              >
                <option value="USD">USD</option>
                <option value="INR">INR</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
            </div>
          </div>

          <div className="form-section-divider">
            <div className="section-header">
              <h3>Employment</h3>
            </div>
          </div>

          {/* Row 4: Approver, Joining Date */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="approverName" className="form-label">
                Approver Name
              </label>
              <input
                type="text"
                id="approverName"
                name="approverName"
                value={formData.approverName}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter approver name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="joiningDate" className="form-label">
                Joining Date
              </label>
              <input
                type="date"
                id="joiningDate"
                name="joiningDate"
                value={formData.joiningDate}
                onChange={handleInputChange}
                className="form-input"
              />
            </div>
          </div>

          <div className="form-section-divider">
            <div className="section-header">
              <h3>Address</h3>
            </div>
          </div>

          {/* Address Section */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="address.street" className="form-label">
                Street
              </label>
              <input
                type="text"
                id="address.street"
                name="address.street"
                value={formData.address.street}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter street address"
              />
            </div>

            <div className="form-group">
              <label htmlFor="address.city" className="form-label">
                City
              </label>
              <input
                type="text"
                id="address.city"
                name="address.city"
                value={formData.address.city}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter city"
              />
            </div>

            <div className="form-group">
              <label htmlFor="address.state" className="form-label">
                State
              </label>
              <input
                type="text"
                id="address.state"
                name="address.state"
                value={formData.address.state}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter state"
              />
            </div>

            <div className="form-group">
              <label htmlFor="address.zipCode" className="form-label">
                Zip Code
              </label>
              <input
                type="text"
                id="address.zipCode"
                name="address.zipCode"
                value={formData.address.zipCode}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter zip code"
              />
            </div>
          </div>

          <div className="form-section-divider">
            <div className="section-header">
              <h3>Additional Information</h3>
            </div>
          </div>

          {/* Notes */}
          <div className="form-group">
            <label htmlFor="notes" className="form-label">
              Notes
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              className="form-input"
              rows="3"
              placeholder="Enter any additional notes"
            />
          </div>

          {/* Action Buttons */}
          <div className="form-actions">
            <button
              type="button"
              onClick={handleReset}
              className="btn-secondary"
            >
              <RotateCcw size={16} />
              Reset
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="btn-primary"
            >
              <Save size={16} />
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default EmployeeMasterForm

